import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import React, { useState } from "react";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import google from "../../../src/images/login/google.png";
import logo from "../../../src/images/signup/logo.png";
import signupleft from "../../../src/images/signup/signup.png";
import Loading from '../../loading';
import { login } from "../../redux/authSlice";

const SignupPage = () => {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmpassword, confirmsetPassword] = useState("");
  const [role, setRole] = useState("user"); // Default to 'user'
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmshowPassword, confirmsetShowPassword] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (password !== confirmpassword) {
      setErrorMessage("Passwords do not match. Please try again.");
      return; // Prevent submission if passwords don't match
    }

    // Simple email format validation
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    // Check password strength
    const passwordPattern =
    /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;  
    if (!passwordPattern.test(password)) {
      setErrorMessage(
        "Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character."
      );
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/signup`, {
        firstname,
        lastname,
        email,
        password,
        role,
      });
      navigate("/login");
    } catch (err) {
      console.error(err);
      setErrorMessage("Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const responseGoogle = async (authResult) => {
    setLoading(true);
    try {
      if (authResult['code']) {
        const response = await axios.get(
          `${API_URL}/api/auth/google?code=${authResult.code}`
        );
  
        const { token, user } = response.data;
  
        if (!user) throw new Error("User not found in response");
        if (user.blocked) {
          alert("User is blocked. Please contact support.");
          return;
        }
  
        dispatch(login({ token, user, role: user.role }));
  
        const expirationTime = Date.now() + 7 * 24 * 60 * 60 * 1000; // Token expiration logic for Google login
  
        localStorage.setItem("authToken", token);
        localStorage.setItem("authUser", JSON.stringify(user));
        localStorage.setItem("authExpiration", expirationTime);
  
        user.role === "admin" ? navigate("/admin") : navigate("/");
      }
    } catch (err) {
      console.error("Error during Google login:", err);
      alert("Failed to log in with Google. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const googleLogin = useGoogleLogin({
    onSuccess: responseGoogle,
    onError: (error) => {
      console.error("Google login error:", error);
      alert("Failed to log in with Google.");
    },
    flow: "auth-code", // Ensures you're using the OAuth2 authorization code flow
  });  

  return (
    <div className="h-screen flex flex-col md:flex-row font-clash">
      {/* Left Section: Image - Visible only on screens larger than 1024px */}
      <div className="hidden lg:block w-2/3">
        <img
          src={signupleft}
          alt="Signup Illustration"
          className="h-full w-full object-cover"
        />
      </div>

      {/* Right Section: Form */}
      <div className="w-full lg:w-1/3 h-screen bg-[#1f1f1f] flex justify-center items-center overflow-hidden p-4 md:p-8">
        <form onSubmit={handleSubmit} className="w-full max-w-md bg-[#1f1f1f] p-8 rounded-md">
          <img src={logo} alt="logo of carvetpro" className="mb-4 mx-auto" />
          <p className="text-left text-[16px] text-white font-semibold mb-4">
            Nice to see you again
          </p>

          <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              id="firstname"
              placeholder="First Name"
              className="w-full py-2 px-3 border border-gray-400 rounded"
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              required
            />
            <input
              type="text"
              id="lastname"
              placeholder="Last Name"
              className="w-full py-2 px-3 border border-gray-400 rounded"
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              required
            />
          </div>

          <div className="mb-3">
            <input
              type="email"
              id="email"
              placeholder="Email address"
              className="w-full py-2 px-3 border border-gray-400 rounded"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="mb-3 relative">
            {/* <label className="block text-[#F0FFC5] text-[11px] m-2" htmlFor="password">
              Password
            </label> */}
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              className="border rounded w-full py-2 px-3 placeholder:text-[#808080] placeholder:text-[14px]"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div
              className="absolute top-3 right-4 cursor-pointer"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <AiOutlineEyeInvisible size={24} /> : <AiOutlineEye size={24} />}
            </div>
          </div>

          <div className="mb-6 relative">
            <input
              type={confirmshowPassword ? "text" : "password"}
              id="confirmPassword"
              className="border rounded w-full py-2 px-3 placeholder:text-[#808080] placeholder:text-[14px]"
              placeholder="Confirm Password"
              value={confirmpassword}
              onChange={(e) => confirmsetPassword(e.target.value)}
              required
            />
            <div
              className="absolute top-2 right-4 cursor-pointer"
              onClick={() => confirmsetShowPassword(!confirmshowPassword)}
            >
              {confirmshowPassword ? <AiOutlineEyeInvisible size={24} /> : <AiOutlineEye size={24} />}
            </div>
          </div>

          {/* Show error message if there's an error */}
          {errorMessage && (
            <p className="text-red-500 text-[12px] mb-2">{errorMessage}</p>
          )}

          {/* <div className="mb-6"> */}
            {/* <label className="block text-[#F0FFC5] text-[11px] mb-2" htmlFor="role">
              Role
            </label> */}
            {/* <select
              id="role"
              className="w-[350px] py-2 px-3 border border-gray-400 rounded"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div> */}

          <div className="mt-2 mb-3 flex items-center">
            <input type="checkbox" id="terms" className="mr-2" required onChange={(e) => setIsTermsAccepted(e.target.checked)}/>
            <label htmlFor="terms" className="text-white text-[12px]">
              Creating your account means you accept our{" "}
              <a href="/terms" className="text-white underline">
                Terms & Conditions
              </a>
            </label>
          </div>
          {loading ? (
            <div>
              <Loading />
            </div>
            ) : (
          <button
            type="submit"
            className={`w-full bg-[#007AFF] text-white py-2 mt-4 rounded hover:bg-blue-600 transition-all duration-300 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={loading || !isTermsAccepted}
          >
              Sign Up
          </button>
        )}

          <hr className="w-full mt-8" />

          <button
            type="button"
            className="bg-[#333333] text-[#fff] text-[14px] py-3 mt-6 px-4 rounded w-full flex items-center justify-center space-x-2"
            onClick={googleLogin}
            disabled={loading}
          >
            <img src={google} alt="Google Logo" className="w-5 h-5" />
            <span>Or sign in with Google</span>
          </button>

          {/* Add Log In Now link */}
          <div className="text-center mt-4">
            <p className="text-white text-[12px]">
              Have an account already?{" "}
              <a href="/login" className="text-blue-500">
                Log In Now
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupPage;
